import { useFormikContext } from "formik";
import { Box, Flex, Text, Button, TextField } from "@radix-ui/themes";
import SelectField from "@/components/form/SelectField";
import CodeField from "@/components/form/CodeField";
import InputField from "@/components/form/InputField";
import { object, InferType, string } from "yup";

// Custom header input component that matches InputField styling
function HeaderInput({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  return (
    <Box>
      <Text as="label" size="2" weight="medium" mb="1">
        {label}
      </Text>
      <TextField.Root
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </Box>
  );
}

export const callAnApiValidationSchema = object({
  parameters: object().shape({
    url: string().required("URL is required").url("Must be a valid URL"),
    method: string()
      .required("Method is required")
      .oneOf(["GET", "POST", "PUT", "DELETE"], "Invalid HTTP method"),
    headers: object()
      .default({})
      .test(
        "headers-type",
        "Headers must be an object with string keys and values",
        (value) =>
          value === undefined ||
          (typeof value === "object" &&
            value !== null &&
            Object.keys(value).every(
              (k) =>
                typeof k === "string" && typeof (value as any)[k] === "string"
            ))
      ) as any, // "as any" needed due to limitations in Yup's object typing for Record<string,string>
    body: string(),
    results_path: string(),
  }),
});

export type CallAnApiFormValues = InferType<typeof callAnApiValidationSchema>;

export default function CallAnApiFields() {
  const { values, setFieldValue } = useFormikContext<CallAnApiFormValues>();

  const handleHeaderKeyChange = (oldKey: string, newKey: string) => {
    if (oldKey === newKey) return;

    const newHeaders = { ...values.parameters.headers };
    const value = newHeaders[oldKey];
    delete newHeaders[oldKey];
    if (newKey.trim()) {
      newHeaders[newKey] = value;
    }
    setFieldValue("parameters.headers", newHeaders);
  };

  const handleHeaderValueChange = (key: string, newValue: string) => {
    const newHeaders = { ...values.parameters.headers };
    newHeaders[key] = newValue;
    setFieldValue("parameters.headers", newHeaders);
  };

  const addHeader = () => {
    const newHeaders = { ...values.parameters.headers };
    let newKey = "";
    let counter = 1;
    while (newHeaders[newKey]) {
      counter++;
      newKey = `header${counter}`;
    }
    newHeaders[newKey] = "";
    setFieldValue("parameters.headers", newHeaders);
  };

  const removeHeader = (key: string) => {
    const newHeaders = { ...values.parameters.headers };
    delete newHeaders[key];
    setFieldValue("parameters.headers", newHeaders);
  };

  return (
    <Box>
      <Flex direction="column" gap="6">
        <InputField name="parameters.url" label="URL" required />

        <SelectField
          name="parameters.method"
          label="Method"
          required
          type="string"
        >
          <SelectField.Item value="GET">GET</SelectField.Item>
          <SelectField.Item value="POST">POST</SelectField.Item>
          <SelectField.Item value="PUT">PUT</SelectField.Item>
          <SelectField.Item value="DELETE">DELETE</SelectField.Item>
        </SelectField>

        <Box>
          <Flex direction="column" gap="4">
            <Text size="2" weight="medium">
              Headers
            </Text>
            {Object.entries(values.parameters.headers || {}).map(
              ([key, value], index) => (
                <Flex key={index} gap="4" align="end">
                  <Box style={{ flex: 1 }}>
                    <HeaderInput
                      label="Header Name"
                      value={key}
                      onChange={(newKey) => handleHeaderKeyChange(key, newKey)}
                      placeholder="e.g. Authorization"
                    />
                  </Box>
                  <Box style={{ flex: 1 }}>
                    <HeaderInput
                      label="Value"
                      value={value as string}
                      onChange={(newValue) =>
                        handleHeaderValueChange(key, newValue)
                      }
                      placeholder="e.g. Bearer token123"
                    />
                  </Box>
                  <Button
                    type="button"
                    color="red"
                    onClick={() => removeHeader(key)}
                  >
                    Remove
                  </Button>
                </Flex>
              )
            )}
            <Button type="button" onClick={addHeader}>
              + Add Header
            </Button>
          </Flex>
        </Box>

        <CodeField
          name="parameters.body"
          label="Body"
          language="application/json"
        />

        <InputField
          name="parameters.results_path"
          label="Results Path"
          description="The path to the results in the response. If not set, the entire response will be used as results."
        />
      </Flex>
    </Box>
  );
}
